import { useCallback } from 'react';
import { useSurah } from '@/hooks/useSurah';
import { useMemorizationSession } from '@/hooks/useMemorizationSession';
import {
  SessionSetup,
  MemorizationView,
  SessionComplete,
} from '@/components/memorize';
import { MemorizeSeo } from '@/components/seo/MemorizeSeo';

/**
 * /memorize — Memorization workflow entry point.
 *
 * Thin orchestrator: reads session state from localStorage via
 * `useMemorizationSession` and branches between three views:
 *
 *   1. SessionSetup       — no active session, user picks surah + range
 *   2. MemorizationView   — active session, current ayah focus
 *   3. SessionComplete    — user stepped past the end ayah
 *
 * All real work happens in the components above and the hook below.
 * No auth dependency. No Supabase writes. No dashboard integration.
 * Future cloud sync swaps the hook's storage layer; this file stays
 * untouched.
 */
export function Memorize() {
  const {
    session,
    isComplete,
    startSession,
    endSession,
    nextAyah,
    prevAyah,
    markMemorized,
    unmarkMemorized,
    setMode,
    setRepeatMode,
  } = useMemorizationSession();

  // For SessionComplete we still need surah metadata.
  const completedSurahQuery = useSurah(
    session && isComplete ? session.surahId : null,
  );

  const handleReviewAgain = useCallback(() => {
    if (!session) return;
    startSession({
      surahId: session.surahId,
      startAyah: session.startAyah,
      endAyah: session.endAyah,
    });
  }, [session, startSession]);

  // ---- Branch 1: no session → setup form ----
  if (!session) {
    return (
      <section className="container-page pt-10 sm:pt-14 pb-16">
        {/* Per-route SEO meta — only renders for the public landing
            view. In-session and complete branches stay private to the
            user and don't need search-engine meta. */}
        <MemorizeSeo />

        <header className="mb-8">
          <p className="text-xs uppercase tracking-[0.22em] text-accent-600 dark:text-accent-400 mb-3">
            Memorize
          </p>
          <h1 className="font-serif text-display-2 text-ink-900 dark:text-ink-50">
            Start a memorization session
            {/* SEO subtitle inside the same H1 so the target keyword
                sits in the page's primary heading without disrupting
                the brand voice. */}
            <span className="block mt-3 text-base font-medium text-ink-600 dark:text-ink-300 sm:text-lg">
              Memorize the Quran online — free Hifz tool with audio recitation
            </span>
          </h1>
          <p className="text-base text-ink-600 dark:text-ink-300 mt-4 max-w-prose leading-relaxed">
            A four-step Hifz workflow that moves ayah by ayah: listen to
            Mishary Rashid Alafasy&rsquo;s recitation, repeat aloud, hide
            the Arabic text, then recall it from memory. No account
            required to begin. Pick any surah and verse range below to
            start your memorization session.
          </p>
        </header>
        <SessionSetup onStart={startSession} />
      </section>
    );
  }

  // ---- Branch 2: cursor past end → complete view ----
  if (isComplete) {
    if (!completedSurahQuery.data) {
      // Loading the surah just for the completion view.
      return (
        <section className="container-prose pt-24 pb-24 text-center">
          <p className="text-ink-500 dark:text-ink-400">Wrapping up…</p>
        </section>
      );
    }
    return (
      <SessionComplete
        surah={completedSurahQuery.data}
        startAyah={session.startAyah}
        endAyah={session.endAyah}
        memorizedCount={session.completedAyahs.length}
        onStartNew={endSession}
        onReviewAgain={handleReviewAgain}
      />
    );
  }

  // ---- Branch 3: active session ----
  return (
    <MemorizationView
      session={session}
      onNextAyah={nextAyah}
      onPrevAyah={prevAyah}
      onMarkMemorized={markMemorized}
      onUnmarkMemorized={unmarkMemorized}
      onModeChange={setMode}
      onRepeatModeChange={setRepeatMode}
      onEndSession={endSession}
    />
  );
}
